import React, { useEffect, useMemo, useState } from "react";
import apiClient from "../api/apiClient";

/**
 * Ventana de destinatarios de recordatorios.
 *
 * Lista los clientes que tienen al menos un contrato y permite tildar /
 * destildar a quién se le envían las notificaciones. Modelo opt-out: por
 * defecto todos reciben; al destildar un cliente se agrega a la lista de
 * `clientes_excluidos` que persiste el backend por empresa.
 *
 * Endpoints:
 *  - GET /recordatorios/clientes  -> { items: [{card_code, card_name, email, contratos, recibe}] }
 *  - PUT /recordatorios/clientes  -> { clientes_excluidos: [CardCode...] }
 */
const RecordatoriosClientes = () => {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [filtro, setFiltro] = useState("");
  const [soloSinEmail, setSoloSinEmail] = useState(false);

  const userCompanyDb = useMemo(() => {
    const user = JSON.parse(localStorage.getItem("sapUser") || "{}");
    return user?.CompanyDB;
  }, []);

  const loadClientes = async () => {
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const res = await apiClient.get("/recordatorios/clientes", {
        params: { CompanyDB: userCompanyDb },
      });
      setClientes(res?.data?.items || []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Error cargando clientes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibles = useMemo(() => {
    const q = filtro.trim().toLowerCase();
    return clientes.filter((c) => {
      if (soloSinEmail && c.email) return false;
      if (!q) return true;
      return (
        String(c.card_name || "").toLowerCase().includes(q) ||
        String(c.card_code || "").toLowerCase().includes(q) ||
        String(c.email || "").toLowerCase().includes(q)
      );
    });
  }, [clientes, filtro, soloSinEmail]);

  const totales = useMemo(() => {
    const reciben = clientes.filter((c) => c.recibe).length;
    const sinEmail = clientes.filter((c) => c.recibe && !c.email).length;
    return { total: clientes.length, reciben, excluidos: clientes.length - reciben, sinEmail };
  }, [clientes]);

  const toggle = (cardCode, value) => {
    setClientes((prev) =>
      prev.map((c) => (c.card_code === cardCode ? { ...c, recibe: value } : c))
    );
  };

  // Aplica un valor de "recibe" a los clientes actualmente visibles (respeta el filtro).
  const setTodosVisibles = (value) => {
    const codes = new Set(visibles.map((c) => c.card_code));
    setClientes((prev) =>
      prev.map((c) => (codes.has(c.card_code) ? { ...c, recibe: value } : c))
    );
  };

  const onSave = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const clientes_excluidos = clientes
        .filter((c) => !c.recibe)
        .map((c) => c.card_code);
      const res = await apiClient.put(
        "/recordatorios/clientes",
        { clientes_excluidos },
        { params: { CompanyDB: userCompanyDb } }
      );
      setClientes(res?.data?.items || clientes);
      setMessage("Destinatarios guardados correctamente.");
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Error guardando destinatarios");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card shadow-sm">
      <div className="card-body">
        <h3 className="card-title mb-2 text-center">Recordatorios de cuotas - Destinatarios</h3>
        <hr className="mt-0 mb-3" />

        <p className="text-muted small">
          Tildá los clientes a quienes querés que les lleguen las notificaciones. Los
          destildados quedan excluidos del envío. Por defecto, todos los clientes con
          contrato reciben.
        </p>

        {error && <div className="alert alert-danger">{error}</div>}
        {message && <div className="alert alert-success">{message}</div>}

        <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
          <span className="badge bg-secondary">Total: {totales.total}</span>
          <span className="badge bg-success">Reciben: {totales.reciben}</span>
          <span className="badge bg-dark">Excluidos: {totales.excluidos}</span>
          {totales.sinEmail > 0 && (
            <span className="badge bg-warning text-dark">
              Tildados sin email: {totales.sinEmail}
            </span>
          )}
          <div className="ms-auto d-flex gap-2">
            <button
              className="btn btn-outline-secondary btn-sm"
              type="button"
              onClick={() => setTodosVisibles(true)}
              disabled={loading || saving}
            >
              Tildar visibles
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              type="button"
              onClick={() => setTodosVisibles(false)}
              disabled={loading || saving}
            >
              Destildar visibles
            </button>
            <button
              className="btn btn-primary btn-sm"
              type="button"
              onClick={onSave}
              disabled={loading || saving}
            >
              {saving ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </div>

        <div className="row g-2 mb-3">
          <div className="col-md-8">
            <input
              className="form-control form-control-sm"
              placeholder="Buscar por nombre, código o email..."
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
            />
          </div>
          <div className="col-md-4 d-flex align-items-center gap-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="chk-solo-sin-email"
                checked={soloSinEmail}
                onChange={(e) => setSoloSinEmail(e.target.checked)}
              />
              <label className="form-check-label small" htmlFor="chk-solo-sin-email">
                Solo sin email
              </label>
            </div>
            <button
              className="btn btn-outline-secondary btn-sm ms-auto"
              type="button"
              onClick={loadClientes}
              disabled={loading || saving}
            >
              Refrescar
            </button>
          </div>
        </div>

        {loading ? (
          <div>Cargando clientes...</div>
        ) : clientes.length === 0 ? (
          <div className="alert alert-info">No hay clientes con contratos.</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm table-striped align-middle">
              <thead>
                <tr>
                  <th style={{ width: 70 }} className="text-center">
                    Recibe
                  </th>
                  <th>Cliente</th>
                  <th>Código</th>
                  <th>Email</th>
                  <th className="text-end">Contratos</th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((c) => (
                  <tr key={c.card_code} className={c.recibe ? "" : "text-muted"}>
                    <td className="text-center">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={!!c.recibe}
                        onChange={(e) => toggle(c.card_code, e.target.checked)}
                      />
                    </td>
                    <td>{c.card_name || <em className="text-muted">(sin nombre)</em>}</td>
                    <td className="small">{c.card_code}</td>
                    <td>
                      {c.email || <span className="text-danger small">sin email</span>}
                    </td>
                    <td className="text-end">{c.contratos}</td>
                  </tr>
                ))}
                {visibles.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted">
                      Ningún cliente coincide con el filtro.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecordatoriosClientes;
